import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { getActivityLogs } from 'reducers/log/actions';
import { toggleAccountMenuDropdown } from 'reducers/customer/actions';
import { FlexPage } from 'components/CommonStyles';
import Table from 'components/Table';
import {
  defaultItemsToDisplayInSingleRow,
  defaultRowHeight,
  defaultSecondaryRowHeight
} from 'utils/appConstants';

const ActivityLogPage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;

const RequestCell = styled.div`
  display: flex;
`;

const RequestType = styled.span`
  padding-right: 10px;
`;

const JsonBody = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1em;
  padding-top: 10px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const JsonBodyLine = styled.span`
  font-family: PT-Mono;
  font-size: 10px;
  white-space: pre-wrap;
  line-height: 14px;
`;

const ResponseCell = styled.div`
  display: flex;
  overflow: auto;
  max-height: 140px;
`;

const ResponseStatusCode = styled.span`
  padding-right: 10px;
  color: ${({ isError }) => (isError ? '#D42D15' : '#0D151F')};
`;

const ResponseMessage = styled.span`
  font-style: italic;
`;

const EmptySubMenuBar = styled.div`
  width: 100%;
  height: 45px;
  background-color: white;
`;

export const activityLogColumnDefs = [
  {
    headerName: 'User',
    field: 'request.user',
    width: 150
  },
  {
    headerName: 'Date',
    field: 'request.timestamp',
    maxWidth: 150
  },
  {
    headerName: 'Endpoint',
    field: 'request.url',
    width: 150
  },
  {
    headerName: 'Request',
    field: 'request',
    comparator: (a, b) => a.method.localeCompare(b.method),
    cellRenderer: ({ value }) => {
      if (!value) {
        return '';
      }
      return (
        <RequestCell>
          <RequestType>{value.method}</RequestType>
          <JsonBody>
            {Object.keys(value.body).map(key => (
              <JsonBodyLine key={value.body[key]}>
                "{key}": {JSON.stringify(value.body[key])}
              </JsonBodyLine>
            ))}
          </JsonBody>
        </RequestCell>
      );
    }
  },
  {
    headerName: 'Response',
    field: 'response',
    comparator: (a, b) => a.status - b.status,
    cellRenderer: ({ value }) => {
      if (!value) {
        return '';
      }
      const statusCode200regex = /2\d{2}/g;
      const isError = !statusCode200regex.test(value?.status);

      return (
        <ResponseCell>
          <ResponseStatusCode isError={isError}>
            {value.status}
          </ResponseStatusCode>
          {value.message ? (
            <ResponseMessage>{value.message}</ResponseMessage>
          ) : Array.isArray(value.body) ? (
            <div>
              {value.body.map(data => (
                <JsonBody>
                  {Object.keys(data).map((key, index) => (
                    <JsonBodyLine key={`${data[key]}-${index}`}>
                      "{key}": {JSON.stringify(data[key])}
                    </JsonBodyLine>
                  ))}
                </JsonBody>
              ))}
            </div>
          ) : (
            <JsonBody>
              {Object.keys(value.body).map((key, index) => (
                <JsonBodyLine key={`${value.body[key]}-${index}`}>
                  "{key}": {JSON.stringify(value.body[key])}
                </JsonBodyLine>
              ))}
            </JsonBody>
          )}
        </ResponseCell>
      );
    }
  }
];

const getRowHeight = ({ data }) => {
  const { request = {}, response = {} } = data;
  const { body: requestBody } = request;
  const { body: responseBody } = response;

  let requestEntries = [];
  let responseEntries = [];
  if (requestBody) {
    requestEntries = Object.entries(requestBody);
  }
  if (responseBody) {
    responseEntries = Object.entries(responseBody);
  }

  // The 'request' or 'response' column could contain multiple lines,
  // so we want to check both of them and see which is bigger and use that
  if (requestEntries.length || responseEntries.length) {
    const setWithMoreEntries =
      requestEntries.length > responseEntries.length
        ? requestEntries
        : responseEntries;
    if (setWithMoreEntries.length > defaultItemsToDisplayInSingleRow) {
      return (
        defaultRowHeight +
        defaultSecondaryRowHeight * defaultItemsToDisplayInSingleRow
      );
    }
    return (
      defaultRowHeight +
      defaultSecondaryRowHeight * (setWithMoreEntries.length - 1)
    );
  }
  return defaultRowHeight;
};

const ActivityLog = () => {
  const dispatch = useDispatch();
  const logs = useSelector(state => state.log.logs) || [];
  const showAcctMenuDropdown = useSelector(
    state => state.customer.showAcctMenuDropdown
  );

  useEffect(() => {
    dispatch(getActivityLogs());
  }, []);

  const toggleMenu = () => {
    if (showAcctMenuDropdown) {
      dispatch(toggleAccountMenuDropdown(false));
    }
  };

  return (
    <FlexPage>
      <ActivityLogPage onClick={toggleMenu}>
        <EmptySubMenuBar />
        <FlexPage>
          <Table
            bigTitle="Activity Log"
            hideCount={true}
            rowData={logs}
            columnDefs={activityLogColumnDefs}
            height="600px"
            width="1000px"
            disableSelect={true}
            getRowHeight={getRowHeight}
          />
        </FlexPage>
      </ActivityLogPage>
    </FlexPage>
  );
};

export default ActivityLog;
